'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/lib/ui-kit/tooltip';

interface ClampedTitleWithTooltipProps {
  children: React.ReactNode;
  className?: string;
  tooltipClassName?: string;
  maxLines?: number;
}

// The v2 tooltip (ui-kit surface, border and padding) at a fixed width: the
// offset below centres it on a card by its half width.
const DEFAULT_TOOLTIP_CLASS =
  'w-[318px] text-[13px] leading-4 tracking-[-0.13px] text-v2-text-primary [overflow-wrap:anywhere]';

export function ClampedTitleWithTooltip({
  children,
  className = '',
  tooltipClassName = DEFAULT_TOOLTIP_CLASS,
  maxLines = 2,
}: ClampedTitleWithTooltipProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [clamped, setClamped] = useState(false);
  const [alignOffset, setAlignOffset] = useState(0);

  const calculateOffset = () => {
    const containerEl = containerRef.current;
    if (!containerEl) return;

    // Find the Card element (parent of CardTitle)
    const cardElement = containerEl.closest('[data-slot="card"]') as HTMLElement;
    if (cardElement) {
      const cardRect = cardElement.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      // Calculate offset: we want tooltip centered on card
      // Using align="start", offset is from the start edge of the trigger
      // We want tooltip center to align with card center
      const cardCenter = cardRect.left + cardRect.width / 2;
      const triggerCenter = containerRect.left + containerRect.width / 2;
      const tooltipHalfWidth = 159; // w-[318px] / 2

      // Calculate how much we need to shift from trigger center to card center
      // Then add that to the offset needed from trigger start
      const shiftFromTriggerCenter = cardCenter - triggerCenter;
      const offsetFromStart = containerRect.width / 2 + shiftFromTriggerCenter - tooltipHalfWidth;

      setAlignOffset(offsetFromStart);
    }
  };

  useLayoutEffect(() => {
    const textEl = textRef.current;
    if (!textEl) return;

    // Check if text is overflowing by comparing scroll height with client height
    const isTextClamped = textEl.scrollHeight > textEl.clientHeight + 2;
    setClamped(isTextClamped);

    // Calculate align offset to center tooltip relative to card
    if (isTextClamped) {
      // Use requestAnimationFrame to ensure DOM is fully laid out
      requestAnimationFrame(() => {
        calculateOffset();
      });

      // Recalculate on resize
      const handleResize = () => {
        calculateOffset();
      };
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [calculateOffset]);

  const textStyles = {
    display: '-webkit-box',
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
  };

  return clamped ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <div ref={containerRef} className={`w-full ${className}`} style={{ display: 'block' }}>
          <div ref={textRef} className={`line-clamp-${maxLines}`} style={textStyles}>
            {children}
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent
        sideOffset={20}
        side="top"
        align="start"
        alignOffset={alignOffset}
        className={tooltipClassName}
        showArrow={false}
      >
        {children}
      </TooltipContent>
    </Tooltip>
  ) : (
    <div ref={textRef} className={`line-clamp-${maxLines} w-full ${className}`} style={textStyles}>
      {children}
    </div>
  );
}
